import { Box, Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconChevronLeft } from '@tabler/icons-react';
import {
  isCompensationValid,
  isEarnestDepositValid,
  isMoneyAskValid,
  isPriceValid,
  isStepComplete,
  type OfferPortalWizardContext,
  type OfferPortalWizardEvent,
  WIZARD_STEP_ORDER,
  WizardStep,
} from 'state_machines/OfferPortalWizardMachine';

import { type ListingDetails } from './listingDetails';
import { SummaryCard } from './SummaryCard';
import { CardHeading, FIELD_INPUT, FieldError, WizardProgress } from './wizardFields';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const FIELD_CARD = 'w-full flex-col gap-4 rounded-[16px] border border-od-border-primary p-4';
const FIELD_LABEL = 'font-medium text-[16px] tracking-[-0.3px] text-od-content-primary';

// The percent input works in fractions of 1 (0.04 for 4%); the draft stores
// basis points.
const BPS_PER_UNIT = 10_000;

/**
 * Step 1 of 6 of the offer wizard — the legacy OfferDetailPage's price and
 * concession terms, per design. The wizard machine owns the answers and the
 * step flow; this component renders the machine's context and sends events.
 */
const OfferTermsStep = ({
  listing,
  context,
  send,
}: {
  listing: ListingDetails;
  context: OfferPortalWizardContext;
  send: (event: OfferPortalWizardEvent) => void;
}) => {
  const maxAllowedCents = listing.max_allowed_offer_price_cents;
  const overCap =
    maxAllowedCents != null && context.priceCents != null && context.priceCents > maxAllowedCents;
  const priceInvalid = (context.showError && !isPriceValid(context)) || overCap;

  const handlePriceChange = (value: number | null) => {
    send({ type: 'SET_PRICE_CENTS', value: value != null ? Math.round(value * 100) : null });
  };

  const earnestOverPrice =
    context.earnestDepositCents != null &&
    context.priceCents != null &&
    context.earnestDepositCents > context.priceCents;
  const earnestInvalid =
    (context.showError && !isEarnestDepositValid(context)) ||
    earnestOverPrice ||
    context.fieldViolations['earnestDepositCents'] != null;
  const concessionInvalid =
    !isMoneyAskValid(context.sellerConcessionCents, context.priceCents) ||
    context.fieldViolations['concessionsFromSellerCents'] != null;
  const compensationInvalid =
    !isCompensationValid(context) || context.fieldViolations['brokerCommissionRate'] != null;

  const handleEarnestChange = (value: number | null) => {
    send({
      type: 'SET_EARNEST_DEPOSIT_CENTS',
      value: value != null ? Math.round(value * 100) : null,
    });
  };

  const handleConcessionChange = (value: number | null) => {
    send({
      type: 'SET_SELLER_CONCESSION_CENTS',
      value: value != null ? Math.round(value * 100) : null,
    });
  };

  const handleCompensationChange = (value: number | null) => {
    send({
      type: 'SET_BUYER_AGENT_COMPENSATION_BPS',
      value: value != null ? Math.round(value * BPS_PER_UNIT) : null,
    });
  };

  const handleContinue = () => {
    send({ type: 'CONTINUE' });
  };

  return (
    <Box as="section" className="w-full flex-col items-center bg-od-bg-primary pb-28">
      <WizardProgress step={1} of={WIZARD_STEP_ORDER.length} />
      {/* The gutter sits outside the max-width so the two columns keep their
          full 784px; padding on the capped element eats into the content. */}
      <Box className="w-full justify-center px-6 pt-10 lg:px-10">
        <Col className="w-full max-w-[784px] gap-8">
          <Col className="gap-4">
            <Box className="w-max">
              <Link
                href="/make-offer/start"
                aria-label="Back to search"
                analyticsName="cosmos-make-offer-back-offer-terms"
                className="flex size-9 items-center justify-center rounded-[10px] bg-od-bg-secondary text-od-content-primary no-underline"
              >
                <IconChevronLeft className="size-5" />
              </Link>
            </Box>
            <Col className="gap-1">
              <Text className="text-body-sm text-od-content-secondary">
                Step 1 of {WIZARD_STEP_ORDER.length}
              </Text>
              <Text
                as="h1"
                className="font-medium text-[32px] tracking-[-0.896px] text-od-content-primary"
              >
                Offer terms
              </Text>
            </Col>
          </Col>

          <Row className="flex-col gap-6 lg:flex-row lg:items-start">
            <Col className="max-w-[493px] flex-1 gap-6">
              <Col className={FIELD_CARD}>
                <CardHeading
                  htmlFor="offer-price"
                  invalid={priceInvalid}
                  title="How much is your client offering?"
                />
                <Input
                  id="offer-price"
                  type="currency"
                  decimals={0}
                  min={0}
                  value={context.priceCents != null ? context.priceCents / 100 : null}
                  onValueChange={handlePriceChange}
                  placeholder="$"
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Offer price"
                  aria-invalid={priceInvalid}
                  error={priceInvalid}
                  analyticsName="cosmos-make-offer-terms-price"
                />
                {overCap && maxAllowedCents != null ? (
                  <Text role="alert" className="text-body-sm text-od-content-error">
                    Offers on this home are capped at {usd.format(maxAllowedCents / 100)}.
                  </Text>
                ) : null}
                {context.fieldViolations['priceCents'] != null ? (
                  <FieldError>{context.fieldViolations['priceCents']}</FieldError>
                ) : null}
                {context.showError && !overCap && !isPriceValid(context) ? (
                  <Text role="alert" className="text-body-sm text-od-content-error">
                    Offer price cannot be empty
                  </Text>
                ) : null}
              </Col>

              <Col className={FIELD_CARD}>
                <CardHeading
                  htmlFor="earnest-deposit"
                  invalid={earnestInvalid}
                  title="How much is the earnest money deposit?"
                  caption="Enter $0 if your client is waiving the deposit."
                />
                <Input
                  id="earnest-deposit"
                  type="currency"
                  decimals={0}
                  min={0}
                  value={
                    context.earnestDepositCents != null ? context.earnestDepositCents / 100 : null
                  }
                  onValueChange={handleEarnestChange}
                  placeholder="$"
                  size="xs"
                  className={FIELD_INPUT}
                  aria-label="Earnest money deposit"
                  aria-invalid={earnestInvalid}
                  error={earnestInvalid}
                  analyticsName="cosmos-make-offer-terms-earnest"
                />
                {context.fieldViolations['earnestDepositCents'] != null ? (
                  <FieldError>{context.fieldViolations['earnestDepositCents']}</FieldError>
                ) : null}
                {earnestOverPrice ? (
                  <FieldError>Earnest money deposit can’t exceed the offer price.</FieldError>
                ) : null}
                {context.showError && context.earnestDepositCents == null ? (
                  <FieldError>Earnest money deposit cannot be empty</FieldError>
                ) : null}
              </Col>

              <Col className={FIELD_CARD}>
                <CardHeading
                  title="Concessions & compensation"
                  caption="Any credits you’re asking the seller to cover, plus the buyer’s agent compensation on this offer. Leave blank if none apply."
                />
                <Col className="gap-2">
                  <Text as="label" htmlFor="seller-concession" className={FIELD_LABEL}>
                    Seller concession (optional)
                  </Text>
                  <Input
                    id="seller-concession"
                    type="currency"
                    decimals={0}
                    min={0}
                    value={
                      context.sellerConcessionCents != null
                        ? context.sellerConcessionCents / 100
                        : null
                    }
                    onValueChange={handleConcessionChange}
                    placeholder="$ 0"
                    size="xs"
                    className={FIELD_INPUT}
                    aria-label="Seller concession"
                    aria-invalid={concessionInvalid}
                    error={concessionInvalid}
                    analyticsName="cosmos-make-offer-terms-concession"
                  />
                  {context.fieldViolations['concessionsFromSellerCents'] != null ? (
                    <FieldError>{context.fieldViolations['concessionsFromSellerCents']}</FieldError>
                  ) : null}
                  {!isMoneyAskValid(context.sellerConcessionCents, context.priceCents) ? (
                    <FieldError>Seller concession can’t exceed the offer price.</FieldError>
                  ) : null}
                </Col>
                <Col className="gap-2">
                  <Text as="label" htmlFor="agent-compensation" className={FIELD_LABEL}>
                    Buyer’s agent compensation (optional)
                  </Text>
                  <Input
                    id="agent-compensation"
                    type="percent"
                    // decimals is fraction precision: 4 places = 1bp steps, so
                    // nothing is silently re-rounded against the stored draft.
                    decimals={4}
                    min={0}
                    // No max: clamping silently rewrote an over-cap number, so
                    // the agent never learned the offer would be refused.

                    value={
                      context.buyerAgentCompensationBps != null
                        ? context.buyerAgentCompensationBps / BPS_PER_UNIT
                        : null
                    }
                    onValueChange={handleCompensationChange}
                    placeholder="0 %"
                    size="xs"
                    className={FIELD_INPUT}
                    aria-label="Buyer’s agent compensation"
                    aria-invalid={compensationInvalid}
                    error={compensationInvalid}
                    analyticsName="cosmos-make-offer-terms-compensation"
                  />
                  {context.fieldViolations['brokerCommissionRate'] != null ? (
                    <FieldError>{context.fieldViolations['brokerCommissionRate']}</FieldError>
                  ) : null}
                  {!isCompensationValid(context) ? (
                    <FieldError>Buyer’s agent compensation can’t exceed 4%.</FieldError>
                  ) : null}
                </Col>
              </Col>
            </Col>

            <SummaryCard
              listing={listing}
              offerCents={context.priceCents}
              continueLabel="Continue"
              continueDisabled={!isStepComplete(WizardStep.OfferTerms, context)}
              onContinue={handleContinue}
              analyticsName="cosmos-make-offer-continue-offer-terms"
            />
          </Row>
        </Col>
      </Box>
    </Box>
  );
};

export default OfferTermsStep;
